import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { LogicielCaisseEntrepotService } from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';
import {
  EtablissementRow,
  LogicielCaisseParametreAssociationsService,
} from 'app/services/logiciel-caisse/logiciel-caisse-parametre-associations.service';
import {
  LogicielCaisseStockGestionService,
  StockLigneParEntrepot,
} from 'app/services/logiciel-caisse/logiciel-caisse-stock-gestion.service';
import { LogicielCaisseGestionStockStateService } from 'app/services/logiciel-caisse/logiciel-caisse-gestion-stock-state.service';
import { LogicielCaisseProduitService } from 'app/services/logiciel-caisse/logiciel-caisse-produit.service';
import { readObservableError } from 'app/services/logiciel-caisse/logiciel-caisse-api.helper';
import { LogicielCaisseStockToolbarComponent } from '../shared/logiciel-caisse-stock-toolbar/logiciel-caisse-stock-toolbar.component';
import { LcStockListFilters } from '../shared/logiciel-caisse-stock-list.types';
import { GestionStockTab } from './logiciel-caisse-gestion-stock.component';

type StockTableRow = Record<string, unknown>;

@Component({
  selector: 'app-logiciel-caisse-gestion-stock-content',
  templateUrl: './logiciel-caisse-gestion-stock-content.component.html',
  styleUrls: ['./logiciel-caisse-gestion-stock-content.component.scss'],
})
export class LogicielCaisseGestionStockContentComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() tab: GestionStockTab = 'actif';
  @ViewChild('toolbar') toolbarCmp?: LogicielCaisseStockToolbarComponent;

  tableRows: StockTableRow[] = [];
  loading = false;

  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  serverTotal = 0;

  stockDetailOpen = false;
  stockDetailLoading = false;
  stockDetailTitle = '';
  stockDetailLines: StockLigneParEntrepot[] = [];

  private filters: LcStockListFilters = {
    searchTerm: '',
    establishmentIds: [],
    warehouseIds: [],
    effectiveWarehouseIds: [],
  };
  private expandedIndexes = new Set<number>();
  private parentRowClickTimer?: ReturnType<typeof setTimeout>;
  private readonly rowClickDelayMs = 280;
  private pageLoadInFlight = false;
  private filterOptionsInFlight = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: LogicielCaisseAuthService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly associationsService: LogicielCaisseParametreAssociationsService,
    private readonly stockService: LogicielCaisseStockGestionService,
    private readonly produitService: LogicielCaisseProduitService,
    private readonly stockState: LogicielCaisseGestionStockStateService,
    private readonly toastr: ToastrService
  ) {}

  ngAfterViewInit(): void {
    this.loadFilterOptions();
    this.refreshFeatherIcons();
  }

  ngOnDestroy(): void {
    this.cancelPendingParentRowClick();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tab'] && !changes['tab'].firstChange) {
      this.currentPage = 1;
      this.expandedIndexes.clear();
      this.loadPage();
    }
  }

  onFiltersChange(filters: LcStockListFilters): void {
    this.filters = filters;
    this.currentPage = 1;
    this.expandedIndexes.clear();
    this.loadPage();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.expandedIndexes.clear();
    this.loadPage();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.expandedIndexes.clear();
    this.loadPage();
  }

  cell(row: StockTableRow, key: string): string {
    const v = row[key];
    return v == null || v === '' ? '—' : String(v);
  }

  hasChildren(row: StockTableRow): boolean {
    const children = row['children'];
    return Array.isArray(children) && children.length > 0;
  }

  childRows(row: StockTableRow): StockTableRow[] {
    const children = row['children'];
    return Array.isArray(children) ? (children as StockTableRow[]) : [];
  }

  isExpanded(index: number): boolean {
    return this.expandedIndexes.has(index);
  }

  toggleExpand(event: Event, index: number, row: StockTableRow): void {
    event.stopPropagation();
    this.toggleExpandAt(index, row);
  }

  onParentRowClick(event: Event, index: number, row: StockTableRow): void {
    if (!this.hasChildren(row) || this.isInteractiveRowTarget(event)) {
      return;
    }
    this.cancelPendingParentRowClick();
    this.parentRowClickTimer = setTimeout(() => {
      this.parentRowClickTimer = undefined;
      this.toggleExpandAt(index, row);
    }, this.rowClickDelayMs);
  }

  onChildRowDblClick(event: Event, child: StockTableRow): void {
    event.preventDefault();
    if (this.isInteractiveRowTarget(event)) {
      return;
    }
    this.openStockDetail(child);
  }

  private isInteractiveRowTarget(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('button, a, input, select, textarea, .chevron');
  }

  private cancelPendingParentRowClick(): void {
    if (this.parentRowClickTimer) {
      clearTimeout(this.parentRowClickTimer);
      this.parentRowClickTimer = undefined;
    }
  }

  private toggleExpandAt(index: number, row: StockTableRow): void {
    if (!this.hasChildren(row)) {
      return;
    }
    if (this.expandedIndexes.has(index)) {
      this.expandedIndexes.delete(index);
    } else {
      this.expandedIndexes.add(index);
    }
    this.refreshFeatherIcons();
  }

  stockQtyBadgeClasses(val: unknown): string[] {
    const n = this.parseStockQtyDisplay(val);
    if (n === null) {
      return [];
    }
    if (n <= 0) {
      return ['badge-rupture'];
    }
    if (n < 10) {
      return ['badge-faible'];
    }
    return ['badge-stock'];
  }

  openStockDetail(row: StockTableRow): void {
    const ids = this.collectProduitIds(row);
    if (ids.length === 0) {
      this.toastr.error('Produit introuvable pour ce détail.');
      return;
    }
    this.stockDetailTitle = this.rowDesignationTitle(row);
    this.stockDetailOpen = true;
    this.stockDetailLoading = true;
    this.stockDetailLines = [];
    const entIds = (this.filters.effectiveWarehouseIds || []).filter(
      (n) => Number.isFinite(n) && n > 0
    );
    this.stockService
      .getStockDetailParEntrepot(ids, entIds.length > 0 ? entIds : undefined)
      .subscribe({
        next: (lines) => {
          this.stockDetailLines = lines;
          this.stockDetailLoading = false;
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.stockDetailLoading = false;
          this.stockDetailOpen = false;
        },
      });
  }

  closeStockDetail(): void {
    this.stockDetailOpen = false;
  }

  formatStockQty(v: number): string {
    return this.produitService.formatStockQty(v);
  }

  get stockDetailTotalPhysique(): number {
    return this.stockDetailLines.reduce((sum, line) => sum + (Number(line.stock_physique) || 0), 0);
  }

  get stockDetailTotalDisponible(): number {
    return this.stockDetailLines.reduce((sum, line) => sum + (Number(line.stock_disponible) || 0), 0);
  }

  private loadFilterOptions(retried = false): void {
    if (this.filterOptionsInFlight) {
      return;
    }
    this.filterOptionsInFlight = true;

    // Toujours vérifier/renouveler le JWT (isReadyForNavigation peut être vrai avec un token expiré).
    this.authService
      .ensureAuthenticatedSession()
      .pipe(
        switchMap((ok) => {
          if (!ok || !this.authService.isAuthenticated()) {
            this.toastr.error(
              'Session logiciel caisse indisponible. Reconnectez-vous ou configurez l’accès caisse.',
              'Gestion stock'
            );
            return of({
              entrepots: [] as { id: string; label: string }[],
              etablissements: [] as EtablissementRow[],
              links: [] as { etablissement_id: number; entrepot_id: number }[],
              tokenExpired: false,
            });
          }

          this.entrepotService.clearEntrepotOptionsCache();
          return forkJoin({
            entrepots: this.entrepotService.getEntrepotOptions().pipe(
              catchError((err: unknown) => {
                const msg = readObservableError(err, 'Erreur chargement entrepôts');
                console.error('[GestionStock] entrepôts', msg, err);
                if (this.isLcTokenError(msg)) {
                  return of({ __tokenExpired: true as const, items: [] as { id: string; label: string }[] });
                }
                this.toastr.error(msg, 'Gestion stock');
                return of({ __tokenExpired: false as const, items: [] as { id: string; label: string }[] });
              }),
              map((v) =>
                Array.isArray(v)
                  ? { __tokenExpired: false as const, items: v }
                  : v
              )
            ),
            etablissements: this.associationsService.listEtablissements(true).pipe(
              catchError((err: unknown) => {
                const msg = readObservableError(err, 'Erreur chargement établissements');
                console.error('[GestionStock] établissements', msg, err);
                if (this.isLcTokenError(msg)) {
                  return of({
                    __tokenExpired: true as const,
                    items: [] as EtablissementRow[],
                  });
                }
                this.toastr.error(msg, 'Gestion stock');
                return of({
                  __tokenExpired: false as const,
                  items: [] as EtablissementRow[],
                });
              }),
              map((v) =>
                Array.isArray(v)
                  ? { __tokenExpired: false as const, items: v }
                  : v
              )
            ),
            links: this.associationsService.listEtablissementEntrepotLinks().pipe(
              catchError(() => of([]))
            ),
          }).pipe(
            map(({ entrepots, etablissements, links }) => {
              const tokenExpired =
                !!(entrepots as { __tokenExpired?: boolean }).__tokenExpired ||
                !!(etablissements as { __tokenExpired?: boolean }).__tokenExpired;
              return {
                entrepots: (entrepots as { items: { id: string; label: string }[] }).items,
                etablissements: (etablissements as { items: EtablissementRow[] }).items,
                links,
                tokenExpired,
              };
            })
          );
        })
      )
      .subscribe({
        next: ({ entrepots, etablissements, links, tokenExpired }) => {
          this.filterOptionsInFlight = false;

          if (tokenExpired && !retried) {
            this.authService.clearSession();
            this.authService.invalidateAccessCache();
            this.loadFilterOptions(true);
            return;
          }
          if (tokenExpired && retried) {
            this.toastr.error(
              'Session logiciel caisse expirée. Reconnectez-vous ou configurez l’accès caisse.',
              'Gestion stock'
            );
          }

          const mapLinks = new Map<number, number[]>();
          for (const link of links || []) {
            const eid = Number((link as { etablissement_id?: number }).etablissement_id);
            const wid = Number((link as { entrepot_id?: number }).entrepot_id);
            if (!Number.isFinite(eid) || !Number.isFinite(wid) || eid <= 0 || wid <= 0) {
              continue;
            }
            const list = mapLinks.get(eid) ?? [];
            list.push(wid);
            mapLinks.set(eid, list);
          }

          const establishmentOptions = (Array.isArray(etablissements) ? etablissements : [])
            .map((row) => ({
              id: Number(row.id),
              label: this.etablissementOptionLabel(row),
            }))
            .filter((o) => Number.isFinite(o.id) && o.id > 0);

          const warehouseOptions = (entrepots || [])
            .map((w) => ({
              id: Number(w.id),
              label: String(w.label || '').trim() || `Entrepôt ${w.id}`,
            }))
            .filter((o) => Number.isFinite(o.id) && o.id > 0);

          this.applyToolbarOptions(mapLinks, establishmentOptions, warehouseOptions);
          this.loadPage();
        },
        error: () => {
          this.filterOptionsInFlight = false;
          this.applyToolbarOptions(new Map(), [], []);
          this.loadPage();
        },
      });
  }

  private isLcTokenError(msg: string): boolean {
    const m = String(msg || '').toLowerCase();
    return (
      (m.includes('token') && (m.includes('invalide') || m.includes('expir'))) ||
      m.includes('jwt') ||
      m.includes('unauthorized') ||
      m.includes('non autoris')
    );
  }

  /** Pousse les options vers la toolbar (retry si ViewChild pas encore dispo). */
  private applyToolbarOptions(
    mapLinks: Map<number, number[]>,
    establishmentOptions: { id: number; label: string }[],
    warehouseOptions: { id: number; label: string }[],
    attempt = 0
  ): void {
    const toolbar = this.toolbarCmp;
    if (!toolbar) {
      if (attempt < 10) {
        setTimeout(
          () =>
            this.applyToolbarOptions(
              mapLinks,
              establishmentOptions,
              warehouseOptions,
              attempt + 1
            ),
          50
        );
      }
      return;
    }
    toolbar.setEntrepotIdsByEtablissement(mapLinks);
    toolbar.setEstablishmentOptions(establishmentOptions);
    toolbar.setWarehouseOptions(warehouseOptions);
    this.cdr.detectChanges();
  }

  private etablissementOptionLabel(etab: EtablissementRow): string {
    const libelle = String(etab?.libelle || '').trim();
    const code = String(etab?.code_etablissement || '').trim();
    if (libelle && code) {
      return `${libelle} (${code})`;
    }
    return libelle || code || `Établissement #${etab?.id}`;
  }

  private loadPage(): void {
    if (!this.authService.isAuthenticated()) {
      if (this.pageLoadInFlight) {
        return;
      }
      this.pageLoadInFlight = true;
      this.authService.ensureAuthenticatedSession().subscribe((ok) => {
        this.pageLoadInFlight = false;
        if (ok) {
          this.loadPage();
        } else {
          this.loading = false;
          this.tableRows = [];
          this.serverTotal = 0;
          this.totalPages = 1;
        }
      });
      return;
    }

    this.loading = true;
    const entrepotIds = (this.filters.effectiveWarehouseIds || []).filter(
      (n) => Number.isFinite(n) && n > 0
    );
    const hasEstablishmentConstraint = (this.filters.establishmentIds || []).length > 0;
    const effectiveIds =
      hasEstablishmentConstraint && entrepotIds.length === 0 ? [-1] : entrepotIds;

    const actif = this.tab !== 'archive';

    forkJoin({
      counts: this.produitService.getProductsCounts(),
      list: this.stockService.getGestionGlobale({
        actif,
        page: this.currentPage,
        limit: this.pageSize,
        byGroup: true,
        entrepotIds: effectiveIds.length ? effectiveIds : undefined,
        searchRef: this.filters.searchTerm || undefined,
        stockPhysMin: this.filters.stockPhysMin,
        stockPhysMax: this.filters.stockPhysMax,
      }),
    }).subscribe({
      next: ({ counts, list }) => {
        this.stockState.setCounts(counts);
        this.tableRows = this.produitService.groupProduitsForTable(list.items);
        this.serverTotal = list.pagination.total;
        this.totalPages = list.pagination.totalPages;
        this.loading = false;
        this.refreshFeatherIcons();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
        if (this.tableRows.length === 0) {
          this.serverTotal = 0;
          this.totalPages = 1;
        }
        this.loading = false;
      },
    });
  }

  private collectProduitIds(row: StockTableRow): number[] {
    const children = row['children'];
    if (Array.isArray(children) && children.length > 0) {
      const ids = children
        .map((c) => Number((c as StockTableRow)['id']))
        .filter((n) => Number.isFinite(n));
      return [...new Set(ids)];
    }
    const id = Number(row['id']);
    return Number.isFinite(id) ? [id] : [];
  }

  private rowDesignationTitle(row: StockTableRow): string {
    const des = row['DESIGNATION'];
    if (typeof des === 'string') {
      const first = des.split('\n')[0]?.trim();
      if (first) {
        return first;
      }
    }
    const ref = row['REFERENCE'];
    return typeof ref === 'string' && ref.trim() !== '' ? ref.trim() : 'Produit';
  }

  private parseStockQtyDisplay(val: unknown): number | null {
    if (val == null || val === '—') {
      return null;
    }
    const s = String(val).replace(/\s/g, '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      try {
        const feather = (window as unknown as { feather?: { replace: () => void } }).feather;
        feather?.replace();
      } catch {
        /* noop */
      }
      this.cdr.markForCheck();
    }, 0);
  }
}
